import { useEffect, useState } from 'react';
import { imageCache } from '../utils/imageCache';

interface UseImagePreloaderOptions {
    preloadOnMount?: boolean; // Whether to start preloading immediately when hook is called
    onProgress?: (loaded: number, total: number) => void; // Progress callback
    onComplete?: () => void; // Completion callback
    onError?: (error: Error) => void; // Error callback
}

interface UseImagePreloaderReturn {
    preloadImages: (sources: string[]) => Promise<void>;
    isLoading: boolean;
    progress: number; // 0-100
    loadedCount: number;
    totalCount: number;
    error: Error | null;
}

/**
 * Custom hook for preloading images with progress tracking
 * 
 * @param imageSources - Array of image URLs to preload
 * @param options - Configuration options
 * @returns Object with preloading state and control functions
 */
export function useImagePreloader(
    imageSources: string[] = [],
    options: UseImagePreloaderOptions = {}
): UseImagePreloaderReturn {
    const {
        preloadOnMount = true,
        onProgress,
        onComplete,
        onError
    } = options;

    const [isLoading, setIsLoading] = useState(false);
    const [loadedCount, setLoadedCount] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [error, setError] = useState<Error | null>(null);

    // Calculate progress percentage
    const progress = totalCount > 0 ? Math.round((loadedCount / totalCount) * 100) : 0;

    const preloadImages = async (sources: string[]): Promise<void> => {
        if (sources.length === 0) return;

        setIsLoading(true);
        setError(null);
        setTotalCount(sources.length);
        setLoadedCount(0);

        try {
            // Filter out already cached images
            const uncachedSources = sources.filter(src => !imageCache.isCached(src));
            
            if (uncachedSources.length === 0) {
                // All images are already cached
                setLoadedCount(sources.length);
                setIsLoading(false);
                onProgress?.(sources.length, sources.length);
                onComplete?.();
                return;
            }

            // Count already cached images
            const alreadyCached = sources.length - uncachedSources.length;
            setLoadedCount(alreadyCached);

            // Preload uncached images one by one to track progress
            let currentLoaded = alreadyCached;
            
            for (const src of uncachedSources) {
                try {
                    await imageCache.preloadImage(src);
                    currentLoaded++;
                    setLoadedCount(currentLoaded);
                    onProgress?.(currentLoaded, sources.length);
                } catch (err) {
                    console.warn(`Failed to preload image: ${src}`, err);
                    // Continue with other images even if one fails
                }
            }

            setIsLoading(false);
            onComplete?.();
        } catch (err) {
            const error = err instanceof Error ? err : new Error('Failed to preload images');
            setError(error);
            setIsLoading(false);
            onError?.(error);
        }
    };

    // Auto-preload on mount if enabled
    useEffect(() => {
        if (preloadOnMount && imageSources.length > 0) {
            preloadImages(imageSources);
        }
    }, [preloadOnMount]); // Only depend on preloadOnMount to avoid re-running when imageSources change

    return {
        preloadImages,
        isLoading,
        progress,
        loadedCount,
        totalCount,
        error
    };
}

/**
 * Simplified hook for basic image preloading without progress tracking
 */
export function useSimpleImagePreloader(imageSources: string[]): {
    preloadImages: () => Promise<void>;
    isLoading: boolean;
    isComplete: boolean;
} {
    const [isLoading, setIsLoading] = useState(true); // Start as loading
    const [isComplete, setIsComplete] = useState(false);

    const preloadImages = async (): Promise<void> => {
        if (imageSources.length === 0) {
            setIsLoading(false);
            setIsComplete(true);
            return;
        }

        setIsLoading(true);
        setIsComplete(false);
        
        try {
            console.log('🖼️ Starting image preload for', imageSources.length, 'images');
            await imageCache.preloadImages(imageSources);
            console.log('✅ Image preload completed successfully');
            setIsComplete(true);
        } catch (error) {
            console.warn('⚠️ Failed to preload some images:', error);
            setIsComplete(true); // Still mark as complete to not block UI
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (imageSources.length > 0) {
            preloadImages();
        } else {
            setIsLoading(false);
            setIsComplete(true);
        }
    }, [imageSources.length]); // Depend on length to trigger when images are provided

    return {
        preloadImages,
        isLoading,
        isComplete
    };
} 